import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

/**
 * Same chrome as `Input` — white, the 1.5px border, 11px radius — with the
 * modify-decision textarea's measurements (`0.94rem` at 1.55 line-height,
 * 13px/15px padding, 120px floor). That field is also the only element in
 * `Visual - Demo.dc.html` with a focus state: its border goes to ink.
 *
 * `resize` exists because the capture-review textareas are pinned
 * (`resize:none`) while the modify-decision one is `resize:vertical`.
 */
const textareaVariants = cva(
  [
    "w-full rounded-input border-[1.5px] border-border bg-surface",
    "px-[15px] py-[13px] font-sans text-[0.94rem] leading-[1.55] text-ink",
    "placeholder:text-faint focus-visible:border-ink",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ],
  {
    variants: {
      resize: {
        vertical: "resize-y",
        none: "resize-none",
      },
      size: {
        /** The modify-decision / journal edit field. */
        default: "min-h-[120px]",
        /** The shorter capture-review notes fields. */
        compact: "min-h-[84px]",
      },
    },
    defaultVariants: { resize: "vertical", size: "default" },
  },
);

type TextareaProps = ComponentProps<"textarea"> &
  VariantProps<typeof textareaVariants>;

export function Textarea({ className, resize, size, ...props }: TextareaProps) {
  return (
    <textarea
      data-slot="textarea"
      className={cn(textareaVariants({ resize, size }), className)}
      {...props}
    />
  );
}

export { textareaVariants };
export type { TextareaProps };
